/**
 * API client · fetch wrapper with auth + 401 auto-refresh
 *
 * 真契约源：apps/api-server/src/api_server/auth/routes.py
 * - login: POST /api/v1/auth/login {email,password,mfa_code?} → LoginResponse
 *   （MFAFIX-1 起响应有两种形状：正常登录带 access/refresh；角色强制 MFA 但未绑定时
 *   只带 enrollment_token + mfa_enrollment_required=true，前端据此跳绑定页）
 * - refresh: POST /api/v1/auth/refresh {refresh_token} → TokenResponse
 * - me: GET /api/v1/auth/me → MeResponse
 * - logout: POST /api/v1/auth/logout (204)
 */
import { useAuthStore } from "@/store/authStore";

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000").replace(/\/$/, "");
const USE_MOCK = (import.meta.env.VITE_USE_MOCK ?? "true").toLowerCase() === "true";

export const apiConfig = { base: API_BASE, useMock: USE_MOCK };

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public detail?: unknown,
  ) {
    super(message);
  }
}

interface RawApiOpts extends RequestInit {
  /** 跳过 token 注入（用于 /login 等公开端点） */
  noAuth?: boolean;
  /** 跳过 401 自动 refresh（避免无限递归 · refresh 本身用） */
  noRefresh?: boolean;
  /**
   * GUARD · 调用方明知在分页（带 UI 分页器的列表页）——本次响应数组"少于总数"是正常的
   * 用户可见分页，不是静默截断，跳过下面的截断检测（防止这类页面被误报刷屏）。
   */
  paged?: boolean;
  /** 需要读响应头时传入（如 X-Total-Count 筛选后总数·2026-08-06 订单列表计数修复）。401 重试后以最终那次为准。 */
  onHeaders?: (h: Headers) => void;
}

// ── GUARD · 静默截断防护（2026-07-30 生产事故后加，见 fetchAllPages 注释）─────────
// 同一 URL 只提示一次，否则 react-query 重复请求会把 console 刷屏。
const _truncationWarned = new Set<string>();
// 后端未加 X-Total-Count 时的启发式兜底：命中这些"整数刚好等于常见默认 limit"值就怀疑被截断。
const _COMMON_DEFAULT_LIMITS = new Set([50, 100, 200, 500, 1000]);

/**
 * 中央截断检测——只加检测，不改任何业务行为，异常也绝不影响正常请求（见 try/catch）。
 * 判定：total 有值（X-Total-Count）→ total > body.length 精确判定；
 *       total 无值 → 有 explicit limit ? body.length===limit : body.length 命中常见默认值集合。
 */
function _checkSilentTruncation(url: string, res: Response, body: unknown, paged?: boolean): void {
  if (paged) return;
  if (!Array.isArray(body)) return;
  if (_truncationWarned.has(url)) return;

  const totalHeader = res.headers.get("X-Total-Count");
  const total = totalHeader != null && totalHeader !== "" ? Number(totalHeader) : null;
  // 🔴 生产/多数 dev 配置 API_BASE 留空 → url 是相对路径（如 "/api/v1/products"），
  // `new URL(url)` 单参数对相对路径会抛 Invalid URL——必须传 base 才能兼容绝对/相对两种。
  const explicitLimitStr = new URL(url, window.location.origin).searchParams.get("limit");
  const explicitLimit = explicitLimitStr != null ? Number(explicitLimitStr) : null;

  let truncated: boolean;
  let totalText: string;
  if (total != null && Number.isFinite(total)) {
    truncated = total > body.length;
    totalText = `总共 ${total} 条`;
  } else if (explicitLimit != null && Number.isFinite(explicitLimit)) {
    truncated = body.length === explicitLimit;
    totalText = "总数未知（后端未带 X-Total-Count）";
  } else {
    truncated = _COMMON_DEFAULT_LIMITS.has(body.length);
    totalText = "总数未知（后端未带 X-Total-Count）";
  }
  if (!truncated) return;

  _truncationWarned.add(url);
  // dev 环境用 console.error（够显眼）；生产环境降级 console.warn，绝不弹 UI 打扰用户。
  const log = import.meta.env.DEV ? console.error : console.warn;
  log(
    `⚠️ 静默截断：GET ${url} 返回 ${body.length} 条但${totalText}。` +
      "若这是选择器/下拉数据源，请改用 fetchAllPages；若是分页列表页，请传 { paged: true } 消除本提示。",
  );
}

/** TENANTBIND-1 · 读 JWT 里的 tenant claim（只解 payload，不校验签名——签名由后端验；拿来决定请求头带哪个租户）。解不出 = null（不带头 = 后端按 default）。 */
export function tenantFromJwt(token: string | null | undefined): string | null {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=")));
    return typeof json?.tenant === "string" && json.tenant ? json.tenant : null;
  } catch {
    return null;
  }
}

/** 内部 fetch · 不做 refresh */
async function rawFetch<T>(path: string, opts: RawApiOpts = {}): Promise<T> {
  const { noAuth, noRefresh: _ignored, paged, headers, onHeaders, ...rest } = opts;
  const token = noAuth ? null : useAuthStore.getState().accessToken;
  // 多租户：带登录用户的 X-Tenant-Slug（后端 TenantMiddleware 据此选租户·缺则回退 default）。
  // 未登录/noAuth（login·refresh·/auth/me 首拉）时不发 → 走 default·与原行为一致。
  // TENANTBIND-1 · 后端要求「令牌租户 == 请求租户」：还没拉到 /auth/me（登录后首拉）时，用令牌自己写的租户，保证两者一致
  const tenantSlug = noAuth ? null : useAuthStore.getState().user?.tenantSlug ?? tenantFromJwt(token);
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const res = await fetch(url, {
    ...rest,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(tenantSlug ? { "X-Tenant-Slug": tenantSlug } : {}),
      ...(headers ?? {}),
    },
  });

  onHeaders?.(res.headers);

  // 204 No Content
  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    let detail: unknown = undefined;
    let message = res.statusText;
    try {
      const body = await res.json();
      detail = body;
      if (typeof body?.detail === "string") message = body.detail;
    } catch {
      // 非 JSON · 忽略
    }
    throw new ApiError(res.status, message, detail);
  }
  const body = (await res.json()) as T;
  try {
    _checkSilentTruncation(url, res, body, paged);
  } catch {
    // 检测逻辑绝不能影响正常请求（如 URL 解析异常）
  }
  return body;
}

/**
 * 业务用 fetch · 401 时自动尝试 refresh 一次再重试
 * 重试仍失败 → 清空 auth + throw
 */
export async function api<T>(path: string, opts: RawApiOpts = {}): Promise<T> {
  try {
    return await rawFetch<T>(path, opts);
  } catch (err) {
    if (
      err instanceof ApiError &&
      err.status === 401 &&
      !opts.noRefresh &&
      !opts.noAuth &&
      useAuthStore.getState().refreshToken
    ) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        return rawFetch<T>(path, { ...opts, noRefresh: true });
      }
    }
    throw err;
  }
}

let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const rt = useAuthStore.getState().refreshToken;
    if (!rt) return false;
    try {
      const res = await rawFetch<{
        access_token: string;
        refresh_token: string;
        expires_in: number;
      }>("/api/v1/auth/refresh", {
        method: "POST",
        body: JSON.stringify({ refresh_token: rt }),
        noAuth: true,
        noRefresh: true,
        // TENANTBIND-1 · 刷新令牌也绑定租户：带上它自己写的租户（单租户 = default，与不带头等价）
        ...(tenantFromJwt(rt) ? { headers: { "X-Tenant-Slug": tenantFromJwt(rt)! } } : {}),
      });
      useAuthStore.getState().setTokens({
        accessToken: res.access_token,
        refreshToken: res.refresh_token,
        expiresIn: res.expires_in,
      });
      return true;
    } catch {
      useAuthStore.getState().logout();
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/** 把 noAuth/noRefresh 选项暴露给 services/auth.ts 使用 */
export { rawFetch };
/** 暴露 refresh 给非 JSON 上传（multipart uploadReceipt）在 401 时刷新 token 重试 */
export { tryRefresh };

/**
 * 翻完所有页 —— **给"选择器/下拉框"类数据用**。
 *
 * 🔴 静默截断防护（2026-07-30 生产事故后加）：后端列表端点普遍是
 * `limit: int = Query(default=50, le=200)`，且响应体**不带总数** —— 调用方拿到 50 条时
 * 无从知道库里还有 80 条。业务员在下拉框里找不到产品/供应商，会以为"数据不存在"。
 * （真实事故：同事手工下单找不到 PRD-00067/00068；供应商 71 家只显示 50 家。）
 *
 * 规则：任何**选择器/下拉/自动补全**的数据源必须走本函数，不许裸调 `listXxx()`。
 * 带 UI 分页的列表页不用（那里的分页是用户可见的，不是静默截断）。
 *
 * @param fetchPage 传入 `(limit, offset) => Promise<T[]>`
 * @param pageSize  单页条数·默认 200（多数端点 `le=200` 的上限）
 */
export async function fetchAllPages<T>(
  fetchPage: (limit: number, offset: number) => Promise<T[]>,
  pageSize = 200,
): Promise<T[]> {
  const out: T[] = [];
  for (let offset = 0; ; offset += pageSize) {
    const page = await fetchPage(pageSize, offset);
    out.push(...page);
    if (page.length < pageSize) break; // 不满一页 = 已到底
    if (offset > 20_000) break;        // 兜底防御·正常业务不可能到达
  }
  return out;
}
